import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ShieldCheck, ShieldOff, ShieldAlert } from "lucide-react";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(3),
    borderRadius: 16,
    background: theme.palette.type === "dark" ? "rgba(18,18,19,0.6)" : "#fff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(91,124,255,0.15)"
      : "1px solid rgba(0,0,0,0.06)",
    marginBottom: theme.spacing(2),
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    minWidth: 0,
  },
  badge: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  badgeRequired: {
    background: theme.palette.type === "dark"
      ? "rgba(16, 185, 129, 0.18)"
      : "rgba(16, 185, 129, 0.12)",
    color: theme.palette.type === "dark" ? "#34d399" : "#059669",
    border: "1px solid rgba(16, 185, 129, 0.3)",
  },
  badgeOptional: {
    background: theme.palette.type === "dark"
      ? "rgba(91, 124, 255, 0.18)"
      : "rgba(91, 124, 255, 0.12)",
    color: theme.palette.type === "dark" ? "#7c9aff" : "#3D5CFD",
    border: "1px solid rgba(91, 124, 255, 0.3)",
  },
  badgeOff: {
    background: theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.04)"
      : "rgba(0, 0, 0, 0.04)",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.45)" : "#94a3b8",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(0,0,0,0.06)",
  },
  textWrap: { display: "flex", flexDirection: "column", minWidth: 0 },
  title: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#f1f5f9" : "#1e293b",
  },
  status: { fontSize: "0.8rem", fontWeight: 500, marginTop: 2 },
  statusRequired: { color: theme.palette.type === "dark" ? "#34d399" : "#059669" },
  statusOptional: { color: theme.palette.type === "dark" ? "#7c9aff" : "#3D5CFD" },
  statusOff: { color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "#94a3b8" },
  desc: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1.5),
    fontSize: "0.82rem",
    lineHeight: 1.5,
  },
  controls: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  graceWrap: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  graceLabel: {
    fontSize: "0.8rem",
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#cbd5e1" : "#475569",
  },
  primaryBtn: {
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
}));

const GRACE_OPTIONS = [3, 7, 14, 30];

const MfaPolicyManager = ({ companyId, initialPolicy }) => {
  const classes = useStyles();
  const [mode, setMode] = useState(initialPolicy?.mode || "off");
  const [grace, setGrace] = useState(initialPolicy?.gracePeriodDays || 7);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialPolicy) {
      setMode(initialPolicy.mode || "off");
      setGrace(initialPolicy.gracePeriodDays || 7);
    }
  }, [initialPolicy]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}/mfa-policy`, {
        mode,
        gracePeriodDays: grace,
      });
      toast.success(i18n.t("mfa.policy.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const badgeClass =
    mode === "required" ? classes.badgeRequired :
    mode === "optional" ? classes.badgeOptional :
    classes.badgeOff;
  const statusClass =
    mode === "required" ? classes.statusRequired :
    mode === "optional" ? classes.statusOptional :
    classes.statusOff;
  const BadgeIcon =
    mode === "required" ? ShieldAlert :
    mode === "optional" ? ShieldCheck :
    ShieldOff;
  const statusLabel =
    mode === "required" ? i18n.t("mfa.policy.modeRequired") :
    mode === "optional" ? i18n.t("mfa.policy.modeOptional") :
    i18n.t("mfa.policy.modeOff");

  return (
    <Paper className={classes.card} elevation={0}>
      <div className={classes.row}>
        <div className={classes.left}>
          <div className={`${classes.badge} ${badgeClass}`}>
            <BadgeIcon size={22} strokeWidth={1.75} />
          </div>
          <div className={classes.textWrap}>
            <span className={classes.title}>{i18n.t("mfa.policy.title")}</span>
            <span className={`${classes.status} ${statusClass}`}>
              {statusLabel}
            </span>
          </div>
        </div>
        <Button
          variant="contained"
          color="primary"
          className={classes.primaryBtn}
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {i18n.t("mfa.setup.confirm")}
        </Button>
      </div>

      <Typography className={classes.desc}>
        {i18n.t("mfa.policy.description")}
      </Typography>

      <Box className={classes.controls}>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel value="off" control={<Radio color="primary" />} label={i18n.t("mfa.policy.modeOff")} />
          <FormControlLabel value="optional" control={<Radio color="primary" />} label={i18n.t("mfa.policy.modeOptional")} />
          <FormControlLabel value="required" control={<Radio color="primary" />} label={i18n.t("mfa.policy.modeRequired")} />
        </RadioGroup>

        {mode === "required" && (
          <div className={classes.graceWrap}>
            <span className={classes.graceLabel}>{i18n.t("mfa.policy.gracePeriod")}</span>
            <Select
              value={grace}
              onChange={(e) => setGrace(Number(e.target.value))}
              variant="outlined"
              style={{ minWidth: 160 }}
            >
              {GRACE_OPTIONS.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </div>
        )}
      </Box>
    </Paper>
  );
};

export default MfaPolicyManager;
